/**
 * Returns true when a search query is wrapped in straight double quotes,
 * indicating an exact-phrase match intent. Mirrors the backend
 * SearchUtilities.IsQuotedPhrase semantics so client-side telemetry and Azure
 * Cognitive Search agree on what counts as a phrase query.
 *
 * Only straight quotes (U+0022) qualify — Azure Search queryType=full ignores
 * curly quotes (U+201C/U+201D) and silently falls back to token matching.
 */
export const isQuotedPhrase = (searchTerm: string | null | undefined): boolean => {
    if (!searchTerm) {
        return false;
    }
    const trimmed = searchTerm.trim();
    if (trimmed.length < 2 || !trimmed.startsWith('"') || !trimmed.endsWith('"')) {
        return false;
    }
    return trimmed.slice(1, -1).trim().length > 0;
};

/**
 * PM-approved placeholder strings for SKP search boxes
 * (work item 5574158, original strings + 2026-05-25 PM revision).
 *
 * Notes preserved from the PM comment:
 *  - En-dash is – (U+2013), not hyphen `-` and not em-dash `—`. Spaces both sides.
 *  - Straight quotes only (U+0022). Azure Search queryType=full only phrase-
 *    matches on straight quotes; curly quotes silently break phrase matching.
 *  - Do not let an editor autocorrect – → — or " → " (smart quotes).
 *
 * Surfaces:
 *  - `homepage`: full-width search bar on the SKP landing/search page.
 *  - `headerExpanded`: narrow header search bar (admin + user) — only shown
 *    once the input is focused (and visually expanded) so the longer copy
 *    does not get clipped in the collapsed state.
 *  - `ticketCreationHint`: parenthetical suffix appended after the contextual
 *    "For example:" placeholder on the request-help / new request page.
 */
export const SEARCH_PLACEHOLDERS = {
    homepage: 'Search articles and tickets – use "quotes" for exact phrase match',
    headerExpanded: 'Search – use "quotes" for exact phrase',
    ticketCreationHint: ' (use "quotes" for exact phrase)'
};
